import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Button,
  Tooltip,
  TextField,
} from "@mui/material";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import BackButton from "../../atoms/Button/BrowserBackButton";
import PageTitle from "../../molecules/PageTitle";
import {
  CustomContainer,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import { useParams } from "react-router-dom";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

const defaultLogLimit = 1000;

// Logの型定義
interface Log {
  ts: number;
  unlockState: string;
  unlockStateCode: number;
  contractId: string;
  contractManagementId: string;
  contractorName: string;
  autoKeyId: number;
  keyIndexInContract: number;
}

// YYYY-MM-DD
const localeDateText = (date: Date) => {
  const y = String(date.getFullYear());
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`
}

const DoorLogPage: React.FC = () => {
  const { autoSdcId } = useParams();
  const [logs, setLogs] = useState<Log[]>([]);
  const [building, setBuilding] = useState<{
    autoBuildingId: number;
    buildingId: string;
    buildingManagementId: string;
  } | undefined>(undefined);
  const [sdc, setSdc] = useState<{
    autoSdcId: number;
    sdcSerialNumber: string;
  } | undefined>(undefined);
  const [logFetchedAt, setLogFetchedAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ログ取得関連パラメータの初期化：取得開始日時は 1 ヶ月（ 30 日）前とする
  const currentDate = new Date();
  const [fromDate, setFromDate] = useState<string>(
    localeDateText(new Date(currentDate.getTime() - 30 * 24 * 60 * 60 * 1000))
  );
  const [untilDate, setUntilDate] = useState<string>(localeDateText(currentDate));
  const [logLimit, setLogLimit] = useState<string>(String(defaultLogLimit));

  const fetchLogs = async () => {
    setIsLoading(true);
    const jwtToken = await getJwtTokenFromIdToken();

    // logLimit のキャップ： 1 以上 10000 未満
    let tmpLogLimit = parseInt(logLimit);
    if (
      !(tmpLogLimit && tmpLogLimit > 0 && tmpLogLimit <= defaultLogLimit * 10)
    ) {
      tmpLogLimit = defaultLogLimit;
      setLogLimit(String(tmpLogLimit));
    }

    let queryParams = `?limit=${tmpLogLimit}`;
    if (fromDate) {
      queryParams += `&from=${new Date(fromDate).getTime()}`;
    }
    if (untilDate) {
      queryParams += `&until=${
        new Date(untilDate).getTime() + 24 * 60 * 60 * 1000 - 1
      }`;
    }

    try {
      // call listAalLogs API
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/sdcs/${autoSdcId}/aal-logs${queryParams}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        },
      );
      const data = await response.json();
      setLogFetchedAt(new Date());
      setLogs(data.aalLogs);
      setBuilding(data.building);
      setSdc(data.sdc);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // unlockStateの値に基づいて表示するテキストを決定する関数
  const translateUnlockState = (state: string) => {
    switch (state) {
      case "Success":
        return "成功";
      case "Error":
        return "エラー";
      default:
        return state; // 予期しない値の場合、そのまま表示
    }
  };

  const translateUnlockStateCode = (code: number) => {
    switch (code) {
      case 0:
        return "UNLOCK";
      case 1:
        return "OPEN";
      case -1:
        return "WRONG_TOKEN";
      case -2:
        return "WRONG_SHA";
      case -3:
        return "OTHERS";
      default:
        return "UNKNOWN";
    }
  };

  const downloadCSV = () => {
    const currentDate = new Date().toLocaleString(); // 現在の日時を取得
    const currentDateRow = [`履歴取得日時: ${currentDate}`]; // 現在の日時を行に追加
    const buildingIdRow = building
      ? [`物件システムID: ${building.buildingId}`]
      : [];
    const buildingManagementIdRow = building
      ? [`取付先コード（物件管理ID）: ${building.buildingManagementId}`]
      : [];
    const sdcSerialNumberRow = sdc ? [`SDCSN: ${sdc.sdcSerialNumber}`] : [];
    const header = [
      "操作日時",
      "操作",
      "操作種別",
      "契約システムID",
      "契約管理ID",
      "契約者氏名",
      "鍵番号",
      "システム鍵番号",
    ];
    const csvRows = logs.map((log) => [
      new Date(log.ts).toLocaleString(),
      translateUnlockState(log.unlockState),
      translateUnlockStateCode(log.unlockStateCode),
      log.contractId,
      log.contractManagementId,
      log.contractorName,
      log.keyIndexInContract,
      log.autoKeyId,
    ]);

    const csvContent = [
      ...currentDateRow,
      ...buildingIdRow,
      ...buildingManagementIdRow,
      ...sdcSerialNumberRow,
      header,
      ...csvRows.map((row) => row.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "autolock-logs.csv";
    link.click();
  };

  // グリッドの列の定義
  const columns: GridColDef[] = [
    {
      field: "ts",
      headerName: "操作日時",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={new Date(params.value).toLocaleString()}>
          <span>{new Date(params.value).toLocaleString()}</span>
        </Tooltip>
      ),
    },
    {
      field: "unlockState",
      headerName: "操作",
      flex: 0.5,
      renderCell: (params) => (
        <Tooltip title={translateUnlockState(params.value)}>
          <span>{translateUnlockState(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: "unlockStateCode",
      headerName: "操作種別",
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Tooltip title={translateUnlockStateCode(params.value)}>
            <span>{translateUnlockStateCode(params.value)}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "contractId",
      headerName: "契約システムID",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "contractManagementId",
      headerName: "契約管理ID",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "contractorName",
      headerName: "契約者氏名",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "keyIndexInContract",
      headerName: "鍵番号",
      flex: 0.5,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "autoKeyId",
      headerName: "システム鍵番号",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  // グリッドの行のデータ
  const rows = logs.map((log, index) => ({
    id: index,
    ...log,
  }));

  return (
    <CustomContainer maxWidth={false}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <PageTitle title="ドア操作履歴" />
        <BackButton />
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        marginBottom={2}
      >
        <Box>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  物件システムID:
                </TableCell>
                <TableCell>{building && building.buildingId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  取付先コード（物件管理ID）:
                </TableCell>
                <TableCell>
                  {building && building.buildingManagementId}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  SDCSN
                </TableCell>
                <TableCell> {sdc && sdc.sdcSerialNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  履歴取得日時:
                </TableCell>
                <TableCell>{logFetchedAt?.toLocaleString() || "-"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            marginRight={6}
          >
            <TextField
              value={logLimit}
              label="取得件数"
              style={{ width: "100px" }}
              variant="outlined"
              color="primary"
              type="number"
              onChange={(event: any) => setLogLimit(event.target.value)}
              size="small"
            />
            <TextField
              label="〜から"
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="〜まで"
              type="date"
              size="small"
              value={untilDate}
              sx={{ marginRight: 2 }}
              onChange={(e) => setUntilDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Button
              onClick={() => fetchLogs()}
              variant="contained"
              color="primary"
            >
              ログ取得
            </Button>
          </Box>
          <Button onClick={downloadCSV} variant="outlined" color="primary">
            CSVダウンロード
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <DataGrid // DataGridの使用
          rows={rows}
          columns={columns}
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : customDataGridLocaleTextNoData
          }
          autoHeight
        />
      </TableContainer>
    </CustomContainer>
  );
};

export default DoorLogPage;
